import React, { useState, useEffect } from "react";
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from "@apollo/client";
import { ConfigProvider } from "antd";
import { useAuth } from "context/AuthContext";
import { useNavProfileBranding } from "hooks/useNavProfileBranding";

import RootRouter from "./routes/RootRouter";
import {
  getApolloClient,
  setNetworkStatusHandlers,
} from "./config/apollo/apollo";
import {
  NetworkStatusProvider,
  useNetworkStatus,
} from "./context/NetworkStatusContext";
import localStorageKeys from "./constants/local-storage-keys";

import "./styles/App.scss";

function App() {
  const { token } = useAuth();
  useNavProfileBranding();
  const [apolloClient, setApolloClient] = useState<
    ApolloClient<NormalizedCacheObject>
  >(getApolloClient(false));

  // Only initialize Apollo integration if we have network status context
  const networkContext = useNetworkStatus();

  useEffect(() => {
    if (networkContext) {
      setNetworkStatusHandlers({
        onOperationStart: networkContext.onOperationStart,
        onOperationSuccess: networkContext.onOperationSuccess,
        onOperationError: networkContext.onOperationError,
      });
    }
  }, [networkContext]);

  useEffect(() => {
    if (token) {
      const storedToken = window.localStorage.getItem(
        localStorageKeys.TOKEN_KEY
      );
      if (storedToken !== token) {
        window.localStorage.setItem(localStorageKeys.TOKEN_KEY, token);
        const newClient = getApolloClient(true, token);
        setApolloClient(newClient);
      }
    } else {
      window.localStorage.removeItem(localStorageKeys.TOKEN_KEY);
      const newClient = getApolloClient(true);
      setApolloClient(newClient);
    }
  }, [token]);

  return (
    <ApolloProvider client={apolloClient}>
      <ConfigProvider theme={{ cssVar: false, hashed: false }}>
        <RootRouter />
      </ConfigProvider>
    </ApolloProvider>
  );
}

// Wrap the entire app with NetworkStatusProvider
function AppWithProvider() {
  return (
    <NetworkStatusProvider>
      <App />
    </NetworkStatusProvider>
  );
}

export default AppWithProvider;
